import { Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import { z } from 'zod';
import {
  SavedItemCreate,
  SavedItemResponse,
  SavedItemsService,
  SavedItemUpdate,
} from 'ldt-state-storage-api';
import { AuthService } from '../../auth/service/auth.service';
import { StateStorageBaseService } from '../../core/services/state-storage-base.service';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { Log } from 'src/app/logger.service';
import {
  StoredItemConfig,
  BaseStoredItem,
} from '../../shared/components/stored-items-browser/stored-items-browser.component';
import { RoleDefinitionItemRendererComponent } from 'src/app/shared/components/role-definition-item-renderer/role-definition-item-renderer.component';
import { UIFilter, UIFilterGroup } from 'src/app/people/quick-build.service';

const RoleCriteriaSchema = z.object({
  mustExact: z.array(z.string()),
  mustFuzzy: z.array(z.string()),
  mustNotExact: z.array(z.string()),
  mustNotFuzzy: z.array(z.string()),
});
export type RoleCriteria = z.infer<typeof RoleCriteriaSchema>;

const RoleDefinitionPayloadSchema = z.object({
  criteria: RoleCriteriaSchema,
  category: z.string().optional(),
});

// This schema represents the final domain model object in the frontend
const RoleDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  category: z.string().optional(),
  createdAt: z.string(),
  createdBy: z.string(),
  criteria: RoleCriteriaSchema,
  scope: z.string(),
});
export type RoleDefinition = z.infer<typeof RoleDefinitionSchema>;

@Injectable({
  providedIn: 'root',
})
export class RoleDefinitionsService
  extends StateStorageBaseService<RoleDefinition>
  implements OnDestroy
{
  roleDefinitions$ = this.items$;

  constructor(
    stateStorageService: SavedItemsService,
    authService: AuthService,
    notificationService: NotificationService
  ) {
    super(stateStorageService, authService, notificationService);
  }

  getResourceType(): string {
    return 'role_definition';
  }

  toApiModel(roleDefinition: RoleDefinition): SavedItemCreate | SavedItemUpdate {
    return {
      name: roleDefinition.name,
      description: roleDefinition.description,
      payload: {
        criteria: roleDefinition.criteria,
        category: roleDefinition.category,
      },
      scope: (roleDefinition.scope || 'org') as 'org' | 'public',
      item_type_name: this.getResourceType(),
    };
  }

  fromApiModel(response: SavedItemResponse): RoleDefinition | null {
    const rawPayload =
      typeof response.payload === 'string' ? JSON.parse(response.payload) : response.payload;

    try {
      const payload = RoleDefinitionPayloadSchema.parse(rawPayload);
      const roleDefinition: RoleDefinition = {
        id: response.id.toString(),
        name: response.name || 'Untitled Role Definition',
        description: response.description || '',
        createdAt: response.created_at,
        createdBy: response.created_by.name || '',
        criteria: payload.criteria,
        category: payload.category,
        scope: response.scope,
      };

      return roleDefinition;
    } catch (error) {
      Log.s('Failed to parse role definition from API', 'error', {
        type: this.getResourceType(),
        id: response.id,
        name: response.name,
        payload: rawPayload,
        error: error instanceof z.ZodError ? error.toString() : String(error),
      });

      return null;
    }
  }

  static toQuickBuildFilterGroup(roleDefinition: RoleDefinition): UIFilterGroup {
    const filters: UIFilter[] = [];
    const criteria = roleDefinition.criteria;

    if (criteria.mustExact.length > 0) {
      filters.push({
        filter: {
          field: 'jobs.title',
          match_type: 'exact',
          string_values: criteria.mustExact,
          type: 'must',
        },
      });
    }

    if (criteria.mustFuzzy.length > 0) {
      filters.push({
        filter: {
          field: 'jobs.title',
          match_type: 'fuzzy',
          string_values: criteria.mustFuzzy,
          type: 'must',
        },
      });
    }

    if (criteria.mustNotExact.length > 0) {
      filters.push({
        filter: {
          field: 'jobs.title',
          match_type: 'exact',
          string_values: criteria.mustNotExact,
          type: 'must_not',
        },
      });
    }

    if (criteria.mustNotFuzzy.length > 0) {
      filters.push({
        filter: {
          field: 'jobs.title',
          match_type: 'fuzzy',
          string_values: criteria.mustNotFuzzy,
          type: 'must_not',
        },
      });
    }
    return {
      operator: 'and',
      filters: filters,
    };
  }

  protected afterCreate(item: RoleDefinition, response: SavedItemResponse): RoleDefinition | null {
    const created = super.afterCreate(item, response);
    if (created) {
      this.sortCachedItems();
    }
    return created;
  }

  protected afterUpdate(item: RoleDefinition, response: SavedItemResponse): RoleDefinition | null {
    const updated = super.afterUpdate(item, response);
    if (updated) {
      this.sortCachedItems();
    }
    return updated;
  }

  private sortCachedItems(): void {
    const items = this.getCachedItems();
    if (items) {
      const sorted = [...items].sort((a, b) => a.name.localeCompare(b.name));
      this.updateCache(sorted);
    }
  }

  public saveList(id: string | null, item: RoleDefinition): Observable<RoleDefinition> {
    return super.saveItem(id, item);
  }

  public deleteList(listId: string): void {
    this.delete(listId).subscribe();
  }

  getBrowserConfig(): StoredItemConfig<RoleDefinition & BaseStoredItem> {
    return {
      title: 'Role Definitions',
      itemTypeName: 'Roles',
      description: 'Select from available role definitions',
      searchPlaceholder: 'Search role definitions...',
      useCategories: true,
      itemCountLabel: (count) => `${count} role${count !== 1 ? 's' : ''}`,
      allowDelete: true,
      onDelete: (item) => this.delete(item.id).subscribe(),
      defaultComponentRenderer: RoleDefinitionItemRendererComponent,
    };
  }

  ngOnDestroy(): void {
    this.destroy();
  }
}
